import { Grid, Switch, TextField } from '@material-ui/core';
import copy from 'copy-to-clipboard';
import { cloneDeep } from 'lodash-es';
import MaterialTable, { Query, QueryResult } from 'material-table';
import React, { createRef, useEffect, useState } from 'react';
import ReactJson, { OnCopyProps } from 'react-json-view';

import { ModelMediumButton } from '~/src/dash/components/model-medium-button';
import { ModelTopBar } from '~/src/dash/components/model-topbar';
import { SearchDropdownSingle } from '~/src/dash/components/search-dropdown-single';
import { ct, Panel, Panels, Text } from '~/src/dash/publicaUI';
import { IMenuList } from '~/src/dash/types/menuList';
import { publicaColors } from '~/src/dash/utils/colors';
import { noopVoid } from '~/src/dash/utils/noop';
import { doesPublisherHavePilotDesk } from '~/src/dash/utils/publishers';
import { hasRoleSuperUser } from '~/src/dash/utils/roles';
import { routeName } from '~/src/dash/utils/routes';
import { useBaseStore } from '~/src/dash/views/base/contexts';
import { RowButton } from '~/src/dash/views/component-view';
import { getChangeLogs } from '~/src/dash/views/publisher/change-logs/api';
import { IChangeLog } from '~/src/dash/views/publisher/change-logs/interfaces';
import { transformChangeLogs } from '~/src/dash/views/publisher/change-logs/transform';
import {
  ActionTakenCreate,
  ActionTakenDelete,
  ActionTakenUpdate,
} from '~/src/dash/views/publisher/change-logs/types';

enum EUserType {
  all = 0,
  normalUser,
  publicAPI,
  pilotDesk,
}

const populateUserTypeList = (publisherID: string): IMenuList => {
  // TODO: Adding translations here would require refactoring as the title is used to key into the list.
  const userTypeList: IMenuList = {
    'All User Types': { title: 'All User Types', selected: false, ID: String(EUserType.all) },
    'Normal User': { title: 'Normal User', selected: true, ID: String(EUserType.normalUser) },
    'Public API': { title: 'Public API', selected: false, ID: String(EUserType.publicAPI) },
  };
  // TODO: ONCE MORE PUBS USE PILOT DESK, THIS WILL HAVE TO BECOME AN API THAT CHECKS ON THE PUBLISHER TO SEE IF THEY HAVE PILOT DESK
  if (doesPublisherHavePilotDesk(publisherID)) {
    userTypeList['Pilot Desk'] = {
      title: 'Pilot Desk',
      selected: false,
      ID: String(EUserType.pilotDesk),
    };
  }
  return userTypeList;
};

export const PublisherChangeLogsView = () => {
  const ref = createRef<any>();
  const { classes, location, session, user } = useBaseStore();
  const { pathname, state } = location as any;

  const [actionTakenFilter, setActionTakenFilter] = useState('All');
  const [actionTakenList, setActionTakenList] = useState({
    'All Actions': { title: 'All Actions', selected: true, ID: 'All' },
    Create: { title: 'Create', selected: false, ID: ActionTakenCreate },
    Update: { title: 'Update', selected: false, ID: ActionTakenUpdate },
    Delete: { title: 'Delete', selected: false, ID: ActionTakenDelete },
  } as IMenuList);
  const [itemIDFilter, setItemIDFilter] = useState(
    state?.resourceID !== undefined ? state.resourceID : '',
  );
  const [itemIDValue, setItemIDValue] = useState(
    state?.resourceID !== undefined ? state.resourceID : '',
  );
  const [resetPage, setResetPage] = useState(false);
  const [showSuperUserChanges, setShowSuperUserChanges] = useState(hasRoleSuperUser(user.user));
  const [tableAffectedFilter, setTableAffectedFilter] = useState('All');
  const [tableChangedList, setTableChangedList] = useState({
    'Adserver Campaigns': { title: 'Adserver Campaigns', selected: false, ID: 'adserver_campaign' },
    'All Tables': { title: 'All Tables', selected: true, ID: 'All' },
    'Bidder Groups': { title: 'Bidder Groups', selected: false, ID: 'bidder_group' },
    'Brand Safety Rules': {
      title: 'Brand Safety Rules',
      selected: false,
      ID: 'brand_safety_rules',
    },
    'Channel Groups': { title: 'Channel Groups', selected: false, ID: 'channel_group' },
    Company: { title: 'Company', selected: false, ID: 'company' },
    'Contextual Segments': {
      title: 'Contextual Segments',
      selected: false,
      ID: 'contextual_segments',
    },
    Deals: {
      title: 'Deals',
      selected: false,
      ID: 'deals',
    },
    'Deals to Bidders Association': {
      title: 'Deals to Bidders Association',
      selected: false,
      ID: 'deal_to_headerbidder_association',
    },
    'Deals to Channels Association': {
      title: 'Deals to Channels Association',
      selected: false,
      ID: 'deal_to_channel_association',
    },
    'Deals to Channel Groups Association': {
      title: 'Deals to Channel Groups Association',
      selected: false,
      ID: 'deal_to_channel_group_association',
    },
    'Deals to Contextual Segments Association': {
      title: 'Deals to Contextual Segments Association',
      selected: false,
      ID: 'deal_to_contextual_segment_association',
    },
    'HBs to Sites Association': {
      title: 'HBs to Sites Association',
      selected: false,
      ID: 'headerbidder_to_channel_association',
    },
    'Header Bidders': { title: 'Header Bidders', selected: false, ID: 'headerbidders' },
    Networks: { title: 'Networks', selected: false, ID: 'networks' },
    Orders: { title: 'Orders', selected: false, ID: 'adserver_order' },
    Publishers: { title: 'Publishers', selected: false, ID: 'publishers' },
    Reports: { title: 'Reports', selected: false, ID: 'reports' },
    Routes: { title: 'Routes', selected: false, ID: 'routes' },
    Sites: { title: 'Sites', selected: false, ID: 'sites' },
    'Smart Floors': {
      title: 'Smart Floors',
      selected: false,
      ID: 'automated_bid_floors',
    },
    'Smart Floor Configs': {
      title: 'Smart Floor Configs',
      selected: false,
      ID: 'smart_floor_configs',
    },
  } as IMenuList);
  const [tableColumns] = useState([
    { title: 'Created At', field: 'CreatedAt', searchable: false },
    { title: 'User', field: 'UserName', searchable: true },
    { title: 'Table Changed', field: 'TableChanged', searchable: true },
    { title: 'Item Changed', field: 'ItemChanged', searchable: true },
    { title: 'Action Taken', field: 'ActionTaken', searchable: true },
  ]);
  const [userIDFilter, setUserIDFilter] = useState('');
  const [userIDValue, setUserIDValue] = useState('');
  const [userTypeList, setUserTypeList] = useState(populateUserTypeList(session.publisher.ID));
  const [userTypeFilter, setUserTypeFilter] = useState(String(EUserType.normalUser));

  useEffect(() => {
    setTableAffectedFilter('All');
  }, []);

  useEffect(() => {
    if (hasRoleSuperUser(user.user)) {
      const clonedTableChangedList: IMenuList = cloneDeep(tableChangedList);
      clonedTableChangedList['Billing Detail Configuration'] = {
        title: 'Billing Detail Configuration',
        selected: false,
        ID: 'billing_detail_configuration',
      };
      clonedTableChangedList['Billing Detail Tiers'] = {
        title: 'Billing Detail Tiers',
        selected: false,
        ID: 'billing_detail_tiers',
      };
      setTableChangedList(clonedTableChangedList);
    }
  }, [tableAffectedFilter]);

  const handleApplyFilter = () => {
    let actionTakenFilter = '';
    for (const { selected, ID } of Object.values(actionTakenList)) {
      if (selected) {
        actionTakenFilter = ID;
        break;
      }
    }
    let tableAffectedFilter = '';
    for (const { selected, ID } of Object.values(tableChangedList)) {
      if (selected) {
        tableAffectedFilter = ID;
        break;
      }
    }

    let userTypeFilter = '';
    for (const { selected, ID } of Object.values(userTypeList)) {
      if (selected) {
        userTypeFilter = ID;
        break;
      }
    }

    ref != null && ref.current !== null ? ref.current.onQueryChange() : noopVoid();

    // when we apply the filter, we want to reset the page count in the table
    setActionTakenFilter(actionTakenFilter);
    setItemIDFilter(itemIDValue);
    setResetPage(true);
    setTableAffectedFilter(tableAffectedFilter);
    setUserIDFilter(userIDValue);
    setUserTypeFilter(userTypeFilter);
  };

  const handleApplyFilterKeyDown = (event: React.KeyboardEvent<HTMLDivElement>) => {
    if (event.key === 'Enter') {
      event.preventDefault();
      event.stopPropagation();
      handleApplyFilter();
    }
  };

  const handleCopy = (copyObj: OnCopyProps) => {
    copy(JSON.stringify(copyObj.src, null, '\t'));
  };

  const handleDropdownChangeActionTakenList = (newActionTakenList: IMenuList) => {
    setActionTakenList(newActionTakenList);
  };

  const handleDropdownChangeTableChangedList = (newTableChangedList: IMenuList) => {
    setTableChangedList(newTableChangedList);
  };

  const handleDropdownChangeUserTypeList = (newUserTypeList: IMenuList): void => {
    setUserTypeList(newUserTypeList);
  };

  const getChangeLogsFromAPI = (query: Query<any>): Promise<QueryResult<any>> => {
    return new Promise((resolve) => {
      getChangeLogs({
        query: {
          publisher_id: session.publisher.ID,
          offset: resetPage ? 0 : query.page * query.pageSize,
          limit: query.pageSize,
          table_affected_filter: tableAffectedFilter,
          action_taken_filter: actionTakenFilter,
          user_id_filter: userIDFilter,
          item_id_filter: itemIDFilter,
          showSuperUserChanges,
          user_type_filter: Number(userTypeFilter),
        },
      }).then((data) => {
        resolve({
          data: transformChangeLogs(data.changeLogs, session.publisher.Localization),
          page: resetPage ? 0 : query.page,
          totalCount: data.totalChangeLogs,
        });
        setResetPage(false);
      });
    });
  };

  return (
    <Panels maxWidth vertical relative minWidthMain>
      <Panel
        largeHorizontalPadding
        verticalPadding
        style={{ borderBottom: `1px solid ${publicaColors.lightGray}` }}
      >
        <ModelTopBar
          title={routeName(pathname)}
          session={session}
          user={user}
          publisher={session.publisher}
          classes={classes}
          delegateElevate={true}
          currentPath={pathname}
          disableTitlePaddingLeft
        />
      </Panel>
      <Panel scroll largePadding>
        <Grid onKeyDown={handleApplyFilterKeyDown}>
          <Grid
            container
            direction='column'
            style={{
              zIndex: 0,
            }}
          >
            <Grid item>
              <Grid container spacing={2} justify='flex-start' alignItems='flex-end'>
                <Grid item lg={2} md={6} sm={12} xs={12} style={{ maxWidth: 180 }}>
                  <TextField
                    label='User ID / Name'
                    name='userIDValue'
                    value={userIDValue}
                    onChange={({ target }) => setUserIDValue(target.value)}
                  />
                </Grid>
                <Grid item lg={2} md={6} sm={12} xs={12} style={{ maxWidth: 180 }}>
                  <TextField
                    label='Item ID / Name'
                    name='itemIDValue'
                    value={itemIDValue}
                    onChange={({ target }) => setItemIDValue(target.value)}
                  />
                </Grid>
                <Grid item lg={3} md={6} sm={12} xs={12} style={{ maxWidth: 240 }}>
                  <SearchDropdownSingle
                    menuTitle='Affected Tables'
                    list={tableChangedList}
                    onChange={noopVoid}
                    onChangeFuncComponent={handleDropdownChangeTableChangedList}
                    stateKey='tableChangedList'
                    menuAcceptClickCallbacks={[]}
                    handleMenuAcceptClick={noopVoid}
                  />
                </Grid>
                <Grid item lg={3} md={6} sm={12} xs={12} style={{ maxWidth: 240 }}>
                  <SearchDropdownSingle
                    menuTitle='Action Taken'
                    list={actionTakenList}
                    onChange={noopVoid}
                    onChangeFuncComponent={handleDropdownChangeActionTakenList}
                    stateKey='actionTakenList'
                    menuAcceptClickCallbacks={[]}
                    handleMenuAcceptClick={noopVoid}
                  />
                </Grid>
                <Grid item lg={2} md={6} sm={12} xs={12} style={{ maxWidth: 240 }}>
                  <SearchDropdownSingle
                    menuTitle={ct('UserType')}
                    list={userTypeList}
                    onChange={noopVoid}
                    onChangeFuncComponent={handleDropdownChangeUserTypeList}
                    stateKey='userTypeList'
                    menuAcceptClickCallbacks={[]}
                    handleMenuAcceptClick={noopVoid}
                  />
                </Grid>
                {hasRoleSuperUser(user.user) ? (
                  <Grid item lg={2} md={6} sm={12} xs={12} style={{ maxWidth: 240 }}>
                    <Text extraSmall muted>
                      Show SuperUser Changes*
                    </Text>
                    <Switch
                      size='small'
                      checked={showSuperUserChanges}
                      onChange={() => setShowSuperUserChanges(!showSuperUserChanges)}
                      color='secondary'
                    />
                  </Grid>
                ) : null}
                <Grid
                  item
                  lg={2}
                  md={6}
                  sm={12}
                  xs={12}
                  style={{ maxWidth: 280, marginLeft: 'auto' }}
                >
                  <RowButton
                    right={
                      <ModelMediumButton
                        disabled={false}
                        variant='contained'
                        color='primary'
                        style={{ paddingLeft: '30px', paddingRight: '30px', whiteSpace: 'nowrap' }}
                        onClick={handleApplyFilter}
                      >
                        Apply Filter
                      </ModelMediumButton>
                    }
                  />
                </Grid>
              </Grid>
            </Grid>
            <Grid item style={{ marginTop: 30 }}>
              <MaterialTable
                tableRef={ref}
                detailPanel={(rowData: IChangeLog) => {
                  if (
                    rowData.ActionTaken === 'Update' ||
                    Object.keys(rowData.ChangesMade).length > 0
                  ) {
                    return (
                      <div style={{ maxWidth: 300 }}>
                        <ReactJson
                          displayDataTypes={false}
                          src={rowData.ChangesMade}
                          enableClipboard={handleCopy}
                        />
                      </div>
                    );
                  } else {
                    return <div>{rowData.ActionTaken + `d item ${rowData.AffectedItemID}`}</div>;
                  }
                }}
                style={{
                  paddingLeft: '20px',
                  paddingRight: '20px',
                }}
                title=''
                options={{
                  filtering: false,
                  search: false,
                  pageSize: 20,
                  pageSizeOptions: [20],
                }}
                columns={tableColumns}
                data={getChangeLogsFromAPI}
              />
            </Grid>
          </Grid>
        </Grid>
      </Panel>
    </Panels>
  );
};
